import React from 'react';
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaRegEnvelope, FaWhatsapp, FaInstagram, FaTiktok } from 'react-icons/fa';
import { IoLogoFacebook } from 'react-icons/io';
import { LuPhone } from 'react-icons/lu';
import { SlLocationPin } from 'react-icons/sl';
import logo from '../assets/Navbar/logo.jpeg';
import footerBg from '../assets/Mahi/109.jpeg';

// Animation variants
const textVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
};

const Footer = () => {
    // Important: use the 'footer' namespace
    const { t } = useTranslation('footer');

    const currentYear = new Date().getFullYear();

    const navigationLinks = [
        { link: '/home', key: 'navHome' },
        { link: '/about-us', key: 'navAbout' },
        { link: '/contact-us', key: 'navContact' },
        { link: '/rooms', key: 'navRooms' },
        { link: '/ayurveda', key: 'navAyurveda' },
    ];

    return (
        <div className="relative bg-[#E0DAC0]">
            {/* Background Image */}
            <div
                className="absolute inset-0 opacity-20"
                style={{
                    backgroundImage: `url(${footerBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            />

            {/* Main Footer Content */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={textVariants}
                className="relative px-5 pt-4 z-10"
            >
                <div className="h-auto rounded-t-lg border-l-[1px] border-r-[1px] border-t-[1px] border-[#1A2F2A]/20 py-10">
                    <div className="container justify-between lg:flex">
                        {/* Column 1: Logo, Social Icons, Description */}
                        <div className="w-full lg:w-1/3">
                            <div className="w-full lg:w-[78%]">
                                <div className="flex justify-center">
                                    <img
                                        src={logo}
                                        alt="Muthumuni Ayurveda Beach Resort Logo"
                                        width={100}
                                        height={80}
                                        className="rounded-lg object-cover"
                                    />
                                </div>
                                <div className="my-5 flex justify-center gap-5">
                                    <a
                                        href="https://www.facebook.com/share/1BfGRTr59L/?mibextid=wwXIfr"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our Facebook page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <IoLogoFacebook size={20} className="text-[#1A2F2A]" />
                                    </a>
                                    <a
                                        href="https://www.instagram.com/mahibeach_ayurveda?igsh=bjExYzI0NWY4azNu"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our Instagram page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <FaInstagram size={20} className="text-[#1A2F2A]" />
                                    </a>
                                    <a
                                        href="https://wa.me/94766111678"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Contact us on WhatsApp"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <FaWhatsapp size={20} className="text-[#1A2F2A]" />
                                    </a>
                                    <a
                                        href="https://www.tiktok.com/@.mahi.beach.ayurv?_r=1&_t=ZS-94BSC88Jb2S"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label="Visit our TikTok page"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <FaTiktok size={20} className="text-[#1A2F2A]" />
                                    </a>
                                    <a
                                        href="mailto:mahibeach23@gmail.com"
                                        aria-label="Email us"
                                        className="transition-all duration-300 hover:scale-125 hover:text-[#E4AB58]"
                                    >
                                        <FaRegEnvelope size={20} className="text-[#1A2F2A]" />
                                    </a>
                                </div>
                                <div className="text-center text-[14px] leading-[22px] sm:text-[16px] sm:leading-[25px] font-light text-[#2D3F3A]">
                                    {t('description')}
                                </div>
                            </div>
                        </div>

                        {/* Column 2: Navigation Links */}
                        <div className="my-8 lg:mt-0 lg:w-1/3 lg:pl-14">
                            <div className="text-center text-[16px] font-medium text-[#1A2F2A] sm:text-[18px] lg:text-left">
                                {t('linksTitle')}
                            </div>
                            <div className="mt-5 flex flex-col items-center gap-2 leading-8 lg:items-start">
                                {navigationLinks.map((item, index) => (
                                    <NavLink
                                        key={index}
                                        to={item.link}
                                        className="w-[180px] text-center text-[14px] font-medium text-[#2D3F3A] transition-colors duration-200 hover:text-[#E4AB58] sm:text-[16px] lg:text-left"
                                        style={({ isActive }) => ({
                                            textDecoration: isActive ? 'underline' : 'none',
                                            textUnderlineOffset: '4px',
                                        })}
                                    >
                                        {t(item.key)}
                                    </NavLink>
                                ))}
                            </div>
                        </div>

                        {/* Column 3: Contact Info */}
                        <div className="flex flex-col items-center text-center lg:w-1/3 lg:items-start lg:pl-14 lg:text-left">
                            <div className="text-[16px] font-medium text-[#1A2F2A] sm:text-[18px]">
                                {t('contactTitle')}
                            </div>
                            <div className="mt-5 leading-10">
                                <div className="flex items-center justify-center gap-3 lg:justify-start">
                                    <LuPhone size={20} className="text-[#1A2F2A]" />
                                    <a
                                        href="tel:+94773036444"
                                        className="text-[14px] font-medium text-[#2D3F3A] transition-colors duration-200 hover:text-[#E4AB58] sm:text-[16px]"
                                    >
                                        +94 77 303 6444
                                    </a>
                                </div>
                                <div className="flex items-center justify-center gap-3 lg:justify-start">
                                    <LuPhone size={20} className="text-[#1A2F2A]" />
                                    <a
                                        href="tel:+94766111678"
                                        className="text-[14px] font-medium text-[#2D3F3A] transition-colors duration-200 hover:text-[#E4AB58] sm:text-[16px]"
                                    >
                                        +94 76 611 1678
                                    </a>
                                </div>
                                <div className="flex items-center justify-center gap-3 lg:justify-start">
                                    <FaRegEnvelope size={20} className="text-[#1A2F2A]" />
                                    <a
                                        href="mailto:mahibeach23@gmail.com"
                                        className="text-[14px] font-medium text-[#2D3F3A] transition-colors duration-200 hover:text-[#E4AB58] sm:text-[16px]"
                                    >
                                        mahibeach23@gmail.com
                                    </a>
                                </div>
                                <div className="flex items-center justify-center gap-3 lg:justify-start">
                                    <SlLocationPin size={20} className="text-[#1A2F2A]" />
                                    <a
                                        href="https://maps.app.goo.gl/338Jvrk1ux6z3RKPA"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[14px] font-medium text-[#2D3F3A] transition-colors duration-200 hover:text-[#E4AB58] sm:text-[16px]"
                                    >
                                        {t('viewLocation')}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Bottom Bar: Copyright & Developer */}
            <div className="relative flex flex-col items-center justify-center gap-1 bg-[#0D4E4A] text-white min-h-[70px] px-4 py-3 sm:min-h-[50px] sm:py-2 z-10">
                <motion.span
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="text-[12px] font-medium sm:text-[14px]"
                >
                    {t('copyright', { year: currentYear })}
                </motion.span>
                <motion.span
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    variants={textVariants}
                    className="text-[12px] font-medium sm:text-[14px]"
                >
                    {t('developedBy')}{' '}
                    <a
                        href="tel:+94764608225"
                        className="transition-colors duration-200 hover:text-[#E4AB58]"
                    >
                        {t('developerName')}
                    </a>
                </motion.span>
            </div>
        </div>
    );
};

export default Footer;